import * as React from 'react';
import * as moment from "moment";
import Note from "../../../model/note/Note";
import DateUtils from "../../../utils/DateUtils";
import {connect, Dispatch} from "react-redux";
import {RootState} from "../../../store/reducer/root/RootState";
import {Icon} from "../../../hiberUiKit/src/atoms/Icon/Icon";
import IconSmall from "../../../hiberUiKit/src/atoms/Icon/IconSmall";
import {deleteNote, modifyPinningOnLeadNote} from "../../../store/action/note/notes";
import Toast from "../../helper/Toast";
import {RouteComponentProps, withRouter} from "react-router";
import UserMention from "../../mention/UserMention";
import CommonStreamItem, {StreamItemAction} from "../../../hiberUiKit/src/molecules/StreamItem/CommonStreamItem";
import {OnFailure, OnSuccess} from "../../../store/action/actionHelpers";
import {notifyBugsnagAndShowToast} from "../../../utils/Utils";
import {option, Option} from "ts-option";

interface ReduxProps {
	/**
	 * Logged user id
	 */
	loggedUserId: string,
	/**
	 * Lead id
	 */
	leadId: Option<string>
}

interface ComponentProps {
	/**
	 * Lead activity to display.
	 */
	activity: Note;
	/**
	 * Indicates if history item is active.
	 */
	isActive: boolean;
	/**
	 * On edit note click.
	 * @param {Note} note note to edit.
	 */
	onEditNoteClick?: (note: Note) => void;
	/**
	 * Indicates if action are disabled
	 */
	readOnly?: boolean;
	/**
	 * Indicates if note is shown not in lead context
	 */
	differentContext?:boolean;
}

interface DispatchProps {
	/**
	 * Delete note action.
	 * @param {string} noteId note identifier.
	 * @param {string} leadId lead identifier.
	 */
	deleteNote: (noteId: string, leadId: string, type: "note" | "reminder") => void
	/**
	 * Pin / unpin note action
	 * @param {string} noteId
	 * @param {string} leadId
	 * @param {boolean} pinned
	 * @param {OnSuccess} onSuccess
	 * @param {OnFailure} onFailure
	 */
	modifyPinningOnLeadNote: (noteId: string, leadId: string, pinned: boolean, onSuccess: OnSuccess, onFailure: OnFailure) => void;
}

interface RouteProps {
	leadReferenceNumber: string
}

type Props = ComponentProps & ReduxProps & DispatchProps & RouteComponentProps<RouteProps>;

class LeadActivityNoteItem extends React.Component<Props, {}> {
	constructor(props: Props) {
		super(props);
		this.renderBodyMessage = this.renderBodyMessage.bind(this);
		this.renderExtraMessage = this.renderExtraMessage.bind(this);
		this.deleteNoteClick = this.deleteNoteClick.bind(this);
		this.editNoteClick = this.editNoteClick.bind(this);
		this.unpinNoteClick = this.unpinNoteClick.bind(this);
		this.pinNoteClick = this.pinNoteClick.bind(this);
	}

	render() {
		let actions:StreamItemAction[] = [];
		if(this.props.activity.creator.id === this.props.loggedUserId && !this.props.readOnly) {
			actions.push(StreamItemAction.new("Edit", this.editNoteClick));
			actions.push(StreamItemAction.new("Delete", this.deleteNoteClick));
			if(!this.props.activity.isReminder() && this.props.activity.pinningData.pinned) {
				actions.push(StreamItemAction.new("Un-pin", this.unpinNoteClick));
			} else if (!this.props.activity.isReminder() && !this.props.activity.pinningData.pinned) {
				actions.push(StreamItemAction.new("Pin to top", this.pinNoteClick));
			}
		}
		return (
			<CommonStreamItem event={this.props.activity}
							  actions={actions}
							  renderBodyMessage={this.renderBodyMessage}
							  renderExtraMessage={this.renderExtraMessage}
							  differentContext={this.props.differentContext}/>
		);
	}

	renderBodyMessage() {
		return (
			<UserMention value={this.props.activity.extendedContent} readonly/>
		)
	}

	renderExtraMessage() {
		return (
			<React.Fragment>
				{this.props.activity.reminderDate.map(date => {
					return date.isAfter(moment()) ? (
						<div className={"m-stream-item_body_reminder"}>
							<IconSmall icon={Icon.Reminder}/> {date.format(DateUtils.DATE_TIME_FORMAT)}
						</div>
					) : "";
				}).orNull}
			</React.Fragment>
		);
	}

	deleteNoteClick() {
		this.props.deleteNote(this.props.activity.id, this.props.leadId.getOrElse(''),
			this.props.activity.reminderDate.isDefined ? "reminder" : "note"
		);
	}

	editNoteClick() {
		this.props.onEditNoteClick && this.props.onEditNoteClick(this.props.activity);
	}

	unpinNoteClick() {
		this.props.modifyPinningOnLeadNote(this.props.activity.id, this.props.leadId.getOrElse(''), false,
			() => Toast.success("Note unpinned successfully.", "Unpin note"),
			(err) => notifyBugsnagAndShowToast(err, 'Unpin note'));
	}

	pinNoteClick() {
		this.props.modifyPinningOnLeadNote(this.props.activity.id, this.props.leadId.getOrElse(''), true,
			() => Toast.success("Note pinned successfully.", "Pin note"),
			(err) => notifyBugsnagAndShowToast(err, 'Pin note'));
	}
}

function mapStateToProps(state: RootState, props: Props) {
	const {leadReferenceNumber} = props.match.params;
	const leadId = option(state.lead.leadsMap[leadReferenceNumber]).map(lead => lead.id);
	return {
		loggedUserId: state.user.user.get.id,
		leadId,
	}
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		deleteNote: (noteId: string, leadId: string, type: "note" | "reminder") => dispatch(
			deleteNote(noteId, leadId,
				() => {Toast.info(`${type} has been deleted`.capitalize(), `Delete ${type}`)},
				() => {Toast.info("Something went wrong. Please try again later.", "Delete note")})
		),
		modifyPinningOnLeadNote: (noteId: string, leadId: string, pinned: boolean, onSuccess: OnSuccess, onFailure: OnFailure) => dispatch(
			modifyPinningOnLeadNote(noteId, leadId, pinned, onSuccess, onFailure)
		)
	}
}

export default withRouter<ComponentProps>(connect(mapStateToProps, mapDispatchToProps)(LeadActivityNoteItem));